import { Fade, Grid, Slide, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import _ from "lodash";
import React from "react";

interface FadeOnMountAnimationProps {
  children: JSX.Element;
  isLoading: boolean;
  direction: "up" | "down" | "left" | "right" | "none";
  fadeTimeOut?: TransitionProps["timeout"];
  slideTimeOut?: TransitionProps["timeout"];
  appear?: boolean;
}

const FadeOnMountAnimation = (props: FadeOnMountAnimationProps) => {
  const { children, direction, isLoading, appear, fadeTimeOut, slideTimeOut } = props;

  const theme = useTheme();

  if (direction === "none") {
    return children;
  }

  return (
    <Fade
      in={isLoading}
      appear={appear}
      timeout={
        fadeTimeOut || {
          enter: 1000,
          exit: theme.transitions.duration.leavingScreen,
        }
      }>
      <div>
        <Slide
          direction={direction}
          in={isLoading}
          mountOnEnter
          timeout={
            slideTimeOut || {
              enter: 500,
              exit: theme.transitions.duration.leavingScreen,
            }
          }>
          <div>{children}</div>
        </Slide>
      </div>
    </Fade>
  );
};

interface CompState {
  [key: string]: boolean | any;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
}

type FadeInDirection = "up" | "down" | "left" | "right" | "none";

interface ComponentLayoutWrapperProps {
  gridArea: string;
  children: JSX.Element | React.ReactNode;
  state: CompState;
  fadeInDirection: FadeInDirection;
  loadingComponent?: JSX.Element;
  errorComponent?: JSX.Element;
  layout?: string[][];
}

const ComponentLayoutWrapper = (props: ComponentLayoutWrapperProps): JSX.Element => {
  const { gridArea, children, state, fadeInDirection, loadingComponent, layout } = props;
  const { isLoading, error } = state;

  const stateOfComponent = React.useMemo((): string => {
    // return which ever property in state is true -> the state of the component
    let currentState: string = "";
    for (const key in state) {
      if (state[key] === true && key === "isLoading") {
        currentState = key;
      }
      if (state[key] === true && key === "isError") {
        currentState = key;
      }
    }
    return currentState;
  }, [state]);

  const errorSelection = React.useMemo((): string => {
    if (!error) return "";
    if (typeof error.data === "string") {
      return error.data;
    }
    if (typeof error.data === "object") {
      return `${error.data.statusCode}: Could not get data from server`;
    }
    return "";
  }, [error]);

  return (
    <>
      {((): JSX.Element => {
        switch (stateOfComponent) {
          case "isLoading":
            return <div style={{ gridArea }}>{loadingComponent}</div>;
          case "isError":
            return (
              <div style={{ gridArea }}>
                <Grid
                  container
                  alignItems="start"
                  justifyContent="center"
                  item
                  sx={{
                    border: "3px solid #D42F2F",
                    borderRadius: "4px",
                    backgroundColor: "#f28585",
                    minHeight: "58px",
                    width: "100%",
                  }}>
                  <Typography color="error.contrastText" variant="caption">
                    {errorSelection}
                  </Typography>
                </Grid>
              </div>
            );
          default:
            if (layout) {
              const flatLayout = _.flatten(layout);
              const displayOrHidden: "block" | "none" = flatLayout.includes(gridArea)
                ? "block"
                : "none";
              return (
                <div style={{ gridArea, display: displayOrHidden }}>
                  <FadeOnMountAnimation direction={fadeInDirection} isLoading={!isLoading}>
                    <>{children}</>
                  </FadeOnMountAnimation>
                </div>
              );
            } else {
              return (
                <div
                  style={{
                    gridArea,
                    alignContent: "start",
                    alignSelf: "start",
                  }}>
                  <FadeOnMountAnimation direction={fadeInDirection} isLoading={!isLoading}>
                    <>{children}</>
                  </FadeOnMountAnimation>
                </div>
              );
            }
        }
      })()}
    </>
  );
};

export default ComponentLayoutWrapper;
